/**
 * FormAccessButton
 *
 * Toolbar button showing the user's access level for the form version on screen
 * ("Full Access" / "Read Only" / "Checking...") and opening `FormAccessModal`.
 *
 * Reads the store through a narrow snapshot — `formId` and `versionId` only —
 * and evaluates access itself via `useEvaluateFormAccess`, never through
 * `useFormStore()`, whose spread of `formVersion.data` would re-render this
 * button on every keystroke in the form.
 *
 * `FormAccessModal` stays mounted while closed so antd drives its open/close
 * transition through `open`. That is only affordable because the modal itself
 * holds no form subscription and `useFormAccess` holds one narrow leaf — it
 * previously carried two `useFormStore()` calls, which put two coarse
 * subscriptions in every form's toolbar for a dialog nobody had opened.
 */

import { FC, useState } from 'react';
import { Button, theme } from 'antd';
import { LockOutlined, UnlockOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { formStore } from '../../../store';
import { useEvaluateFormAccess } from '../../../use/data/useEvaluateFormAccess';
import FormAccessModal from '../../modals/FormAccessModal';

const FormAccessButton: FC = () => {
    const { token } = theme.useToken();
    const snap = useSnapshot(formStore.state);
    const formVersionAccess = useEvaluateFormAccess({
        formId: snap.formId,
        versionId: snap.versionId,
        enabled: true,
    });

    const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);

    /**
     * Determines the appropriate icon and text for the access control button
     * based on the current user's permissions for the form version.
     *
     * @returns Object containing icon component and display text
     */
    const getAccessButtonProps = () => {
        if (formVersionAccess.canWrite) {
            return {
                icon: <UnlockOutlined />,
                text: 'Full Access',
            };
        }
        if (formVersionAccess.canRead) {
            return {
                icon: <LockOutlined />,
                text: 'Read Only',
            };
        }
        return {
            icon: <LoadingOutlined />,
            text: 'Checking...',
        };
    };

    const accessButtonProps = getAccessButtonProps();

    return (
        <>
            <Button
                type='text'
                style={{
                    border: `${token.lineWidth}px solid ${token.colorBorder}`,
                    backgroundColor: token.colorBgBase
                }}
                icon={accessButtonProps.icon}
                onClick={() => setIsAccessModalOpen(true)}
                aria-label={accessButtonProps.text}
            >
                {accessButtonProps.text}
            </Button>
            <FormAccessModal
                open={isAccessModalOpen}
                onClose={() => setIsAccessModalOpen(false)}
            />
        </>
    );
};

export default FormAccessButton;
